/**
 * Instagram Reels — desktop hover play; mobile tap play + swipe scroll.
 */
class InstagramReels extends HTMLElement {
  /** @type {MediaQueryList} */
  #mobileMq = window.matchMedia('(max-width: 749px)');

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLButtonElement | null} */
  #prevBtn = null;

  /** @type {HTMLButtonElement | null} */
  #nextBtn = null;

  /** @type {number} */
  #pointerX = 0;

  /** @type {number} */
  #pointerY = 0;

  /** @type {boolean} */
  #didSwipe = false;

  /** @type {number} */
  #lastScrollLeft = 0;

  get #isMobile() {
    return this.#mobileMq.matches;
  }

  connectedCallback() {
    this.#track = this.querySelector('[data-reel-track]');
    this.#prevBtn = this.querySelector('[data-reel-prev]');
    this.#nextBtn = this.querySelector('[data-reel-next]');
    this.#lastScrollLeft = this.#track?.scrollLeft || 0;

    this.querySelectorAll('[data-reel-card]').forEach((card) => {
      this.#bindCard(/** @type {HTMLElement} */ (card));
    });

    this.#prevBtn?.addEventListener('click', () => this.#scrollBy(-1));
    this.#nextBtn?.addEventListener('click', () => this.#scrollBy(1));
    this.#track?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#track?.addEventListener('pointerdown', this.#onPointerDown, { passive: true });
    this.#track?.addEventListener('pointermove', this.#onPointerMove, { passive: true });
    this.#track?.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.#track?.addEventListener('touchmove', this.#onTouchMove, { passive: true });
    window.addEventListener('resize', this.#onScroll);
    document.addEventListener('click', this.#onDocumentClick);
    this.#mobileMq.addEventListener?.('change', this.#onViewportChange);

    this.#updateNav();
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#track?.removeEventListener('pointerdown', this.#onPointerDown);
    this.#track?.removeEventListener('pointermove', this.#onPointerMove);
    this.#track?.removeEventListener('touchstart', this.#onTouchStart);
    this.#track?.removeEventListener('touchmove', this.#onTouchMove);
    window.removeEventListener('resize', this.#onScroll);
    document.removeEventListener('click', this.#onDocumentClick);
    this.#mobileMq.removeEventListener?.('change', this.#onViewportChange);
  }

  #onScroll = () => {
    this.#updateNav();

    if (!this.#isMobile || !this.#track) return;

    const delta = Math.abs(this.#track.scrollLeft - this.#lastScrollLeft);
    this.#lastScrollLeft = this.#track.scrollLeft;

    // Finger swipe scroll — pause active reel so swipe feels native
    if (delta > 2 && this.#didSwipe) {
      this.#pauseAll();
    }
  };

  #onViewportChange = () => {
    if (!this.#isMobile) this.#pauseAll();
    this.#updateNav();
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    this.#pointerX = event.clientX;
    this.#pointerY = event.clientY;
    this.#didSwipe = false;
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (!this.#isMobile) return;
    const dx = Math.abs(event.clientX - this.#pointerX);
    const dy = Math.abs(event.clientY - this.#pointerY);
    if (dx > 10 || dy > 10) this.#didSwipe = true;
  };

  /**
   * @param {TouchEvent} event
   */
  #onTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch) return;
    this.#pointerX = touch.clientX;
    this.#pointerY = touch.clientY;
    this.#didSwipe = false;
  };

  /**
   * @param {TouchEvent} event
   */
  #onTouchMove = (event) => {
    if (!this.#isMobile) return;
    const touch = event.touches[0];
    if (!touch) return;
    const dx = Math.abs(touch.clientX - this.#pointerX);
    const dy = Math.abs(touch.clientY - this.#pointerY);
    if (dx > 10 || dy > 10) this.#didSwipe = true;
  };

  /**
   * Mobile: tap outside any reel card → stop playing video.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#isMobile) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-reel-card]')) return;
    if (event.target.closest('[data-reel-prev], [data-reel-next]')) return;
    this.#pauseAll();
  };

  /**
   * @param {number} direction
   */
  #scrollBy(direction) {
    const track = this.#track;
    if (!track) return;

    // Button scroll should not be treated as a finger swipe
    this.#didSwipe = false;

    const card = track.querySelector('[data-reel-card]');
    const gap = Number.parseFloat(getComputedStyle(track).gap || '12') || 12;
    const step =
      card instanceof HTMLElement ? card.getBoundingClientRect().width + gap : Math.max(240, track.clientWidth * 0.7);

    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  #updateNav() {
    const track = this.#track;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const canScroll = maxScroll > 4;
    const atStart = track.scrollLeft <= 4;
    const atEnd = track.scrollLeft >= maxScroll - 4;

    this.#setNavState(this.#prevBtn, canScroll && !atStart);
    this.#setNavState(this.#nextBtn, canScroll && !atEnd);
  }

  /**
   * @param {HTMLButtonElement | null} btn
   * @param {boolean} enabled
   */
  #setNavState(btn, enabled) {
    if (!btn) return;
    btn.disabled = !enabled;
    btn.classList.toggle('is-disabled', !enabled);
    btn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
  }

  /**
   * @param {HTMLElement} card
   */
  #bindCard(card) {
    const video = card.querySelector('.instagram-reels__video');
    const progress = card.querySelector('[data-reel-progress]');
    const seek = card.querySelector('[data-reel-seek]');

    if (!(video instanceof HTMLVideoElement)) return;

    video.playsInline = true;
    video.muted = true;
    video.loop = true;
    video.preload = 'metadata';

    card.addEventListener('mouseenter', () => {
      if (this.#isMobile) return;
      this.#play(card, video, true);
    });

    card.addEventListener('mouseleave', () => {
      if (this.#isMobile) return;
      this.#stop(card, video, progress);
    });

    card.addEventListener('click', (event) => {
      if (!this.#isMobile) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-reel-seek]')) return;
      if (event.target.closest('a')) return;

      // Finger swipe ended on a card — don't toggle play
      if (this.#didSwipe) {
        this.#didSwipe = false;
        return;
      }

      const wasPlaying = card.classList.contains('is-playing');
      this.#pauseAll();

      // Same card again → stop. Other card → start that one.
      if (!wasPlaying) {
        this.#play(card, video, true);
      }
    });

    if (seek instanceof HTMLElement) {
      seek.addEventListener('click', (event) => {
        event.stopPropagation();
        if (this.#didSwipe) return;
        const seekTrack = seek.querySelector('.instagram-reels__seek-track');
        if (!(seekTrack instanceof HTMLElement) || !video.duration) return;
        const rect = seekTrack.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
        video.currentTime = video.duration * percent;
      });
    }

    video.addEventListener('timeupdate', () => {
      if (!(progress instanceof HTMLElement) || !video.duration) return;
      progress.style.width = `${(video.currentTime / video.duration) * 100}%`;
    });

    video.addEventListener('ended', () => {
      if (progress instanceof HTMLElement) progress.style.width = '0%';
      video.currentTime = 0;
      if (card.classList.contains('is-playing') || (!this.#isMobile && card.matches(':hover'))) {
        window.setTimeout(() => {
          video.play().catch(() => {});
        }, 80);
      }
    });
  }

  #pauseAll() {
    this.querySelectorAll('[data-reel-card]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const video = el.querySelector('.instagram-reels__video');
      const progress = el.querySelector('[data-reel-progress]');
      if (video instanceof HTMLVideoElement) {
        this.#stop(el, video, progress);
      }
    });
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLVideoElement} video
   * @param {boolean} preferSound
   */
  #play(card, video, preferSound) {
    card.classList.add('is-playing');
    video.currentTime = 0;

    const tryPlay = (muted) => {
      video.muted = muted;
      return video.play();
    };

    if (preferSound) {
      tryPlay(false).catch(() => {
        tryPlay(true).catch(() => {});
      });
    } else {
      tryPlay(true).catch(() => {});
    }
  }

  /**
   * @param {HTMLElement} card
   * @param {HTMLVideoElement} video
   * @param {Element | null} progress
   */
  #stop(card, video, progress) {
    card.classList.remove('is-playing');
    video.pause();
    video.currentTime = 0;
    video.muted = true;
    if (progress instanceof HTMLElement) progress.style.width = '0%';
  }
}

if (!customElements.get('instagram-reels')) {
  customElements.define('instagram-reels', InstagramReels);
}
